// CreateCourseModal — full course creation: Master Catalog copy, Community
// Library clone, or Custom course. Self-contained (own tokens, own queries).
// Duplicated from EducationAdminPage's internal pickers (vocab'd) until the
// dedupe sitting — after which both pages import THIS file.
import React, { useState, useEffect, useMemo } from 'react'
import { Loader2, Plus, Sparkles, BookOpen, ChevronDown, X, Eye, Search, Check } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { DEFAULT_STREAMS, classifyStream, classVisibleInStreams, loadOrgCatalogStreams, UNIVERSITY_FACILITY_ID } from '@/utils/catalogStreams'
import type { CatalogStream } from '@/utils/catalogStreams'
import { useFacilityBranding, useOrgVocab } from '@/contexts/SubdomainBrandingContext'
import { renderFormattedText, shouldShowAge } from '@/utils/textClean'
import CoursePreviewModal from './CoursePreviewModal'
import type { EduClass } from '@/hooks/useEducationAdmin'

// Dark tokens (match EducationAdminPage's dark-first family)
const EDU = {
    sage: '#4a7c5e', sageLight: 'rgba(74, 124, 94, 0.2)', sageDark: '#2d5a3d',
    navy: '#f8fafc', navyMid: '#94a3b8', cream: 'rgba(15,23,42,0.5)',
    white: '#0f172a', slate: '#94a3b8', muted: '#94a3b8',
    border: 'rgba(255,255,255,0.08)', blue: '#38bdf8',
}
const btnGrad = `linear-gradient(135deg, ${EDU.sage}, ${EDU.sageDark})`

interface EduCategory {
    id: string; name: string; icon: string
    educational_level: string | null; parent_id: string | null
    slug: string | null; target_demographics: any
}

// ─── Catalog Picker — matches SoloSubjectsPage search/stream/drill-down pattern ─────
const CAT_GROUP_ICONS: Record<string, string> = {
    'Mathematics': '📐',
    'Sciences': '🔬',
    'English & Writing': '📝',
    'Languages': '🌐',
    'Humanities': '📜',
    'Social Sciences': '👥',
    'Technology & Computing': '💻',
    'Arts & Performance': '🎨',
    'Business & Finance': '💼',
    'Life Skills & Wellbeing': '🧘',
    'Christian Studies': '✝️',
    'Careers & Study Skills': '🧭',
    'Astronomy & Space': '🔭',
    'Elite Academy': '👑',
    'General': '📚',
    'Bible Journey': '📖',
    'Faith Stream': '✝️',
    'Electives & Practical Arts': '🌍',
}

const CAT_GROUP_ORDER = [
    'Elite Academy', 'Mathematics', 'Sciences', 'English & Writing', 'Languages',
    'Humanities', 'Social Sciences', 'Technology & Computing', 'Arts & Performance',
    'Business & Finance', 'Life Skills & Wellbeing', 'Christian Studies', 'Bible Journey',
    'Careers & Study Skills', 'Astronomy & Space', 'Electives & Practical Arts', 'General',
]

const AGE_RANGES = [
    { key: 'all', label: 'All Ages', min: 0, max: 99 },
    { key: '5-7', label: '5–7 yrs', min: 5, max: 7 },
    { key: '8-11', label: '8–11 yrs', min: 8, max: 11 },
    { key: '12-14', label: '12–14 yrs', min: 12, max: 14 },
    { key: '15-18', label: '15–18 yrs', min: 15, max: 18 },
    { key: 'adult', label: 'Adult / All (18+)', min: 18, max: 99 },
]

type StreamKey = 'all' | 'standard' | 'elite' | 'university' | 'church' | 'community'

const STREAM_PILLS: { key: StreamKey; label: string; icon: string }[] = [
    { key: 'all', label: 'All', icon: '🌐' },
    { key: 'standard', label: 'Standard', icon: '📚' },
    { key: 'elite', label: 'Elite Academy', icon: '👑' },
    { key: 'university', label: 'University', icon: '🎓' },
    { key: 'church', label: 'Bible Journey', icon: '📖' },
    { key: 'community', label: 'Electives', icon: '🌍' },
]

const STREAM_COLORS: Record<string, string> = {
    elite: '#f59e0b',
    university: '#a78bfa',
    church: '#d97706',
    community: '#4a7c5e',
    standard: '#38bdf8',
}

function CourseCatalogPicker({ onSubmit, loading, hubName }: {
    onSubmit: (vals: { name: string; description: string; target_age: number | null; category_id: string; source_class_id: string }) => Promise<void>
    loading: boolean
    hubName?: string | null
}) {
    const [courses, setCourses] = useState<any[]>([])
    const [catLoading, setCatLoading] = useState(true)
    const [selected, setSelected] = useState<any | null>(null)
    const [className, setClassName] = useState('')
    const [search, setSearch] = useState('')
    const [streamFilter, setStreamFilter] = useState<StreamKey>('all')
    const [selectedDept, setSelectedDept] = useState<string>('all')
    const [ageRange, setAgeRange] = useState<string>('all')
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(CAT_GROUP_ORDER.slice(0, 4)))
    const [preview, setPreview] = useState<any | null>(null)
    const vocab = useOrgVocab()
    const branding = useFacilityBranding()
    const showAge = shouldShowAge(branding?.org_type)

    useEffect(() => {
        Promise.all([
            // Master K-12 catalog — facility 0001
            supabase
                .from('classes')
                .select('id, name, category_id, subject_group, target_age, curriculum_track, description, in_church_catalog, facility_id, categories(name)')
                .eq('facility_id', '00000000-0000-0000-0000-000000000001')
                .eq('is_active', true)
                .order('name'),
            // University catalog — facility 0002
            supabase
                .from('classes')
                .select('id, name, category_id, subject_group, target_age, curriculum_track, description, in_church_catalog, facility_id, categories(name)')
                .eq('facility_id', UNIVERSITY_FACILITY_ID)
                .eq('is_active', true)
                .order('name'),
            // Community library electives
            supabase
                .from('community_course_library' as any)
                .select('id, name, subject_group, target_age, category_id, description, author_name')
                .order('name')
                .limit(80),
        ]).then(([clsRes, uniRes, comRes]: any[]) => {
            const masterList = (clsRes.data || []).map((c: any) => ({
                ...c,
                category_name: c.categories?.name || 'General',
                source: 'master' as const,
            }))
            const uniList = (uniRes.data || []).map((c: any) => ({
                ...c,
                category_name: c.categories?.name || 'General',
                source: 'master' as const,
            }))
            const comList = (comRes.data || []).map((c: any) => ({
                ...c,
                category_name: c.subject_group || 'Electives & Practical Arts',
                subject_group: c.subject_group || 'Electives & Practical Arts',
                curriculum_track: 'community',
                source: 'community' as const,
            }))

            const combined = [...masterList, ...uniList, ...comList].filter((c: any) => {
                const n = (c.name || '').toLowerCase()
                return !n.includes('pastoral ministry')
            })

            setCourses(combined)
            setCatLoading(false)
        })
    }, [])

    const [streams, setStreams] = useState<CatalogStream[]>(DEFAULT_STREAMS)
    const { userProfile } = useAuth()
    useEffect(() => { loadOrgCatalogStreams(userProfile?.facility_id).then(setStreams) }, [userProfile?.facility_id])

    const streamCourses = courses.filter(c => classVisibleInStreams(c, streams))

    // Stream counts (Standard 84, Elite 30, University 45, Faith 9, Electives)
    const streamCounts: Record<string, number> = { all: streamCourses.length }
    for (const c of streamCourses) {
        const s = classifyStream(c)
        streamCounts[s] = (streamCounts[s] || 0) + 1
    }

    // Available departments / subject groups from visible courses
    const availableDepts = useMemo(() => {
        const set = new Set<string>()
        for (const c of streamCourses) {
            const g = (c as any).subject_group || 'General'
            set.add(g)
        }
        return CAT_GROUP_ORDER.filter(g => set.has(g)).concat(Array.from(set).filter(g => !CAT_GROUP_ORDER.includes(g)))
    }, [streamCourses])

    // Filter by Search, Stream, Department, and Age Range
    const filtered = useMemo(() => {
        const range = AGE_RANGES.find(r => r.key === ageRange) || AGE_RANGES[0]
        const q = search.toLowerCase().trim()
        return streamCourses.filter(c => {
            const catName = ((c as any).category_name || '').toLowerCase()
            const subGroup = ((c as any).subject_group || '').toLowerCase()
            const name = (c.name || '').toLowerCase()
            const matchSearch = !q || name.includes(q) || subGroup.includes(q) || catName.includes(q)

            const cs = classifyStream(c)
            const matchStream = streamFilter === 'all' || cs === streamFilter

            const currentGroup = (c as any).curriculum_track === 'faith' || cs === 'church' ? 'Bible Journey' : ((c as any).subject_group || 'General')
            const matchDept = selectedDept === 'all' || currentGroup === selectedDept || (c as any).subject_group === selectedDept

            const matchAge = ageRange === 'all' || !c.target_age ||
                cs === 'church' ||
                (c.target_age >= range.min && c.target_age <= range.max)

            return matchSearch && matchStream && matchDept && matchAge
        })
    }, [streamCourses, search, streamFilter, selectedDept, ageRange])

    // Grouping by subject group
    const grouped = useMemo(() => {
        const map = new Map<string, any[]>()
        for (const c of filtered) {
            const isFaithTrack = (c as any).curriculum_track === 'faith' || classifyStream(c) === 'church'
            const displayGroup = isFaithTrack ? 'Bible Journey' : ((c as any).subject_group || 'General')
            const arr = map.get(displayGroup) || []
            arr.push(c)
            map.set(displayGroup, arr)
        }
        const sortItems = (g: string, items: any[]) =>
            g === 'Bible Journey'
                ? [...items].sort((a, b) => a.id.localeCompare(b.id))
                : [...items].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        return CAT_GROUP_ORDER
            .filter(g => map.has(g))
            .map(g => ({ group: g, items: sortItems(g, map.get(g)!) }))
            .concat(
                Array.from(map.entries())
                    .filter(([g]) => !CAT_GROUP_ORDER.includes(g))
                    .map(([g, items]) => ({ group: g, items: sortItems(g, items) }))
            )
    }, [filtered])

    // Expand all groups automatically when searching
    useEffect(() => {
        if (search.trim()) {
            setExpandedGroups(new Set(grouped.map(g => g.group)))
        }
    }, [search])

    const toggleGroup = (g: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev)
            next.has(g) ? next.delete(g) : next.add(g)
            return next
        })
    }

    const expandAll = () => setExpandedGroups(new Set(grouped.map(g => g.group)))
    const collapseAll = () => setExpandedGroups(new Set())

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!selected) return
        await onSubmit({
            name: className || `${selected.name}: ${hubName || vocab.hubLabel}`,
            description: selected.description || '',
            target_age: selected.target_age ?? null,
            category_id: selected.category_id!,
            source_class_id: selected.id,
        })
    }

    const B = EDU.border
    const pill = (active: boolean, color?: string): React.CSSProperties => ({
        padding: '5px 12px', borderRadius: 999,
        border: `1.5px solid ${active ? (color || EDU.sage) : B}`,
        background: active ? (color || EDU.sage) : 'transparent',
        cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
        color: active ? '#fff' : EDU.navyMid, flexShrink: 0,
        transition: 'all 0.15s ease',
        display: 'inline-flex', alignItems: 'center', gap: 5,
    })

    return (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Search Input */}
            <div style={{ position: 'relative' }}>
                <Search size={15} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: EDU.muted, pointerEvents: 'none' }} />
                <input
                    type="text"
                    placeholder="Search by course name, category, or keywords (e.g. Physics, Algebra, Python)…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{
                        width: '100%', height: 42, paddingLeft: 38, paddingRight: 12,
                        borderRadius: 11, border: `1.5px solid ${B}`, fontSize: 13,
                        outline: 'none', boxSizing: 'border-box',
                        background: 'rgba(255,255,255,0.04)', color: EDU.navy,
                    }}
                />
            </div>

            {/* School / Stream Pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: EDU.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginRight: 2 }}>School:</span>
                {STREAM_PILLS.map(p => {
                    const count = streamCounts[p.key] ?? 0
                    if (p.key !== 'all' && count === 0) return null
                    const cColor = STREAM_COLORS[p.key] || EDU.sage
                    return (
                        <button
                            key={p.key}
                            type="button"
                            onClick={() => setStreamFilter(p.key)}
                            style={pill(streamFilter === p.key, cColor)}
                        >
                            <span>{p.icon}</span>
                            <span>{p.label} ({count})</span>
                        </button>
                    )
                })}
            </div>

            {/* Department Drill-down & Age Filters */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, minWidth: 260 }}>
                    {/* Department / Category Dropdown */}
                    <div style={{ flex: 1 }}>
                        <select
                            value={selectedDept}
                            onChange={e => setSelectedDept(e.target.value)}
                            style={{
                                width: '100%', height: 34, padding: '0 10px', borderRadius: 8,
                                border: `1px solid ${B}`, background: 'rgba(255,255,255,0.05)',
                                color: EDU.navy, fontSize: 12, fontWeight: 600, outline: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            <option value="all" style={{ background: '#0f172a', color: '#fff' }}>All Departments / Subjects</option>
                            {availableDepts.map(d => (
                                <option key={d} value={d} style={{ background: '#0f172a', color: '#fff' }}>
                                    {(CAT_GROUP_ICONS[d] || '📚')} {d}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Age Range Filter */}
                    <div style={{ width: 130 }}>
                        <select
                            value={ageRange}
                            onChange={e => setAgeRange(e.target.value)}
                            style={{
                                width: '100%', height: 34, padding: '0 10px', borderRadius: 8,
                                border: `1px solid ${B}`, background: 'rgba(255,255,255,0.05)',
                                color: EDU.navy, fontSize: 12, fontWeight: 600, outline: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            {AGE_RANGES.map(r => (
                                <option key={r.key} value={r.key} style={{ background: '#0f172a', color: '#fff' }}>
                                    {r.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Expand / Collapse Controls */}
                <div style={{ display: 'flex', gap: 6, fontSize: 11 }}>
                    <button type="button" onClick={expandAll} style={{ border: 'none', background: 'transparent', color: EDU.blue, cursor: 'pointer', padding: '2px 4px', fontWeight: 700 }}>
                        Expand All
                    </button>
                    <span style={{ color: EDU.border }}>·</span>
                    <button type="button" onClick={collapseAll} style={{ border: 'none', background: 'transparent', color: EDU.muted, cursor: 'pointer', padding: '2px 4px', fontWeight: 700 }}>
                        Collapse
                    </button>
                </div>
            </div>

            {/* Grouped Course Cards List */}
            <div style={{ maxHeight: 380, overflowY: 'auto', border: `1.5px solid ${B}`, borderRadius: 13, background: 'rgba(0,0,0,0.22)' }}>
                {catLoading ? (
                    <div style={{ padding: 36, textAlign: 'center', color: EDU.muted, fontSize: 13 }}>
                        <Loader2 size={20} className="animate-spin" style={{ margin: '0 auto 8px', display: 'block' }} />
                        Loading courses &amp; subjects…
                    </div>
                ) : grouped.length === 0 ? (
                    <div style={{ padding: 36, textAlign: 'center', color: EDU.muted, fontSize: 13 }}>
                        No courses match — try a different search keyword, stream, or age filter.
                    </div>
                ) : grouped.map(({ group, items }) => {
                    const isOpen = expandedGroups.has(group)
                    const icon = CAT_GROUP_ICONS[group] || '📚'
                    return (
                        <div key={group} style={{ borderBottom: `1px solid ${B}` }}>
                            <button
                                type="button"
                                onClick={() => toggleGroup(group)}
                                style={{
                                    width: '100%', display: 'flex', alignItems: 'center',
                                    justifyContent: 'space-between', gap: 10, padding: '10px 14px',
                                    background: isOpen ? 'rgba(255,255,255,0.02)' : 'transparent',
                                    border: 'none', cursor: 'pointer', textAlign: 'left',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 16 }}>{icon}</span>
                                    <span style={{ fontWeight: 800, fontSize: 13, color: EDU.navy }}>{group}</span>
                                    <span style={{ fontSize: 10.5, color: EDU.muted, background: 'rgba(255,255,255,0.06)', padding: '1px 7px', borderRadius: 10, fontWeight: 700 }}>
                                        {items.length}
                                    </span>
                                </div>
                                <ChevronDown size={14} style={{
                                    color: EDU.muted, flexShrink: 0,
                                    transform: isOpen ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.18s ease',
                                }} />
                            </button>

                            {isOpen && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 7, padding: '2px 10px 12px' }}>
                                    {items.map(c => {
                                        const isSel = selected?.id === c.id
                                        const cs = classifyStream(c)
                                        const sc = STREAM_COLORS[cs] || '#38bdf8'
                                        const streamName = cs === 'church' ? 'Faith' : cs === 'community' ? 'Elective' : cs === 'university' ? 'University' : cs === 'elite' ? 'Elite' : 'Standard'
                                        return (
                                            <div
                                                key={c.id}
                                                onClick={() => {
                                                    setSelected(c)
                                                    setClassName(`${c.name}: ${hubName || vocab.hubLabel}`)
                                                }}
                                                style={{
                                                    padding: '10px 12px', borderRadius: 10, textAlign: 'left',
                                                    border: `1.5px solid ${isSel ? EDU.sage : 'rgba(255,255,255,0.08)'}`,
                                                    background: isSel ? `${EDU.sage}20` : 'rgba(255,255,255,0.03)',
                                                    cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6,
                                                    position: 'relative', transition: 'all 0.15s ease',
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                                    <div style={{
                                                        fontWeight: 700, fontSize: 12.5, color: EDU.navy, lineHeight: 1.35,
                                                        display: '-webkit-box', WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical', overflow: 'hidden', flex: 1,
                                                    }}>
                                                        {renderFormattedText(c.name || '')}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); setPreview(c) }}
                                                        title="Preview full course outline"
                                                        style={{
                                                            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                                                            color: EDU.navy, padding: '3px 7px', borderRadius: 7, fontSize: 10.5,
                                                            fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
                                                        }}
                                                    >
                                                        <Eye size={11} /> Outline
                                                    </button>
                                                </div>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                                    <span style={{
                                                        fontSize: 9, fontWeight: 800, padding: '1.5px 6px', borderRadius: 20,
                                                        background: `${sc}22`, color: sc, border: `1px solid ${sc}40`,
                                                        textTransform: 'uppercase', letterSpacing: '0.04em',
                                                    }}>
                                                        {streamName}
                                                    </span>
                                                    {c.target_age && (
                                                        <span style={{ fontSize: 9.5, color: EDU.muted, background: 'rgba(255,255,255,0.05)', padding: '1.5px 6px', borderRadius: 20 }}>
                                                            Age {c.target_age}+
                                                        </span>
                                                    )}
                                                    {c.category_name && c.category_name !== group && c.category_name !== 'General' && (
                                                        <span style={{ fontSize: 9.5, color: EDU.slate, background: 'rgba(255,255,255,0.04)', padding: '1.5px 6px', borderRadius: 20, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>
                                                            {c.category_name}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {/* Selected Course Summary */}
            {selected && (
                <div style={{ border: `1.5px solid ${EDU.sage}60`, background: `${EDU.sage}12`, borderRadius: 12, padding: '11px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: selected.description ? 6 : 0 }}>
                        <span style={{ fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em', color: EDU.sage, display: 'flex', alignItems: 'center', gap: 5 }}>
                            <Check size={13} /> Selected · {selected.name}
                        </span>
                        <button
                            type="button"
                            onClick={() => setPreview(selected)}
                            style={{
                                border: 'none', background: btnGrad, color: '#fff',
                                padding: '6px 12px', borderRadius: 9, fontWeight: 700,
                                fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
                            }}
                        >
                            <Eye size={12} /> Preview Outline
                        </button>
                    </div>
                    {selected.description && (
                        <p style={{ margin: 0, fontSize: 12, color: EDU.slate, lineHeight: 1.55 }}>
                            {renderFormattedText((selected.description || '').slice(0, 180))}
                            {(selected.description || '').length > 180 ? '…' : ''}
                        </p>
                    )}
                </div>
            )}

            {preview && (
                <CoursePreviewModal
                    course={{
                        id: preview.id,
                        name: preview.name,
                        source: preview.source || 'master',
                        description: preview.description,
                        subject_group: preview.subject_group,
                        curriculum_track: preview.curriculum_track,
                        target_age: preview.target_age,
                    }}
                    onClose={() => setPreview(null)}
                />
            )}

            {/* Course Name Input */}
            <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: EDU.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 5 }}>
                    {vocab.courseSingular} Name
                </label>
                <input
                    type="text"
                    value={className}
                    onChange={e => setClassName(e.target.value)}
                    style={{
                        width: '100%', height: 44, padding: '0 14px', borderRadius: 11,
                        border: `1.5px solid ${B}`, fontSize: 13.5, outline: 'none',
                        boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)', color: EDU.navy,
                    }}
                    placeholder={`e.g. ${selected ? selected.name : 'Catalog course'}: ${hubName || vocab.hubLabel}`}
                />
            </div>

            {/* Submit Button */}
            <button
                type="submit"
                disabled={loading || !selected}
                style={{
                    padding: 14, borderRadius: 12, background: btnGrad, color: '#fff',
                    border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer',
                    opacity: (loading || !selected) ? 0.6 : 1, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
            >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                Create {vocab.courseSingular} from Catalog
            </button>
        </form>
    )
}

// ─── Custom Course Form — topic → subject → name ──────────────
function CreateClassForm({ onSubmit, loading }: {
    onSubmit: (vals: { name: string; description: string; target_age: number | null; category_id: string }) => Promise<void>
    loading: boolean
}) {
    const [categories, setCategories] = useState<EduCategory[]>([])
    const [catLoading, setCatLoading] = useState(true)
    const vocab = useOrgVocab()
    const [selectedParentId, setSelectedParentId] = useState<string | null>(null)
    const [selectedCat, setSelectedCat] = useState<EduCategory | null>(null)
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [targetAge, setTargetAge] = useState('')
    const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null)

    useEffect(() => {
        // Full taxonomy v3 tree: roots (school types) → departments → topics.
        // Planted rows carry the 70000000- id family; adopted children
        // (legacy ids parented under planted nodes) qualify via their ancestors.
        supabase
            .from('categories')
            .select('id, name, icon, educational_level, parent_id, target_demographics')
            .eq('is_educational', true)
            .order('name')
            .then(({ data }) => {
                const all = (data || []) as EduCategory[]
                const byId = new Map(all.map(c => [c.id, c] as [string, EduCategory]))
                const inTree = (c: EduCategory): boolean => {
                    let n: EduCategory | undefined = c
                    while (n) {
                        if (n.id.startsWith('70000000-')) return true
                        n = n.parent_id ? byId.get(n.parent_id) : undefined
                    }
                    return false
                }
                setCategories(all.filter(inTree))
                setCatLoading(false)
            })
    }, [])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!selectedCat) return
        // Use whatever the user typed; fall back to category name only if truly blank
        const finalName = name.trim() || selectedCat.name
        await onSubmit({
            name: finalName,
            description,
            target_age: targetAge ? parseInt(targetAge) : null,
            category_id: selectedCat.id,
        })
    }

    const inputStyle: React.CSSProperties = {
        width: '100%', height: 48, padding: '0 14px', borderRadius: 12,
        border: `2px solid ${EDU.border}`, fontSize: 15, outline: 'none',
        boxSizing: 'border-box', background: EDU.white, color: EDU.navy,
    }

    const labelStyle: React.CSSProperties = {
        fontSize: 11, fontWeight: 700, color: EDU.muted,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        display: 'block', marginBottom: 6,
    }

    if (catLoading) {
        return <div style={{ padding: 32, textAlign: 'center', color: EDU.muted, fontSize: 13 }}>
            <Loader2 size={20} className="animate-spin" style={{ margin: '0 auto 8px', display: 'block' }} /> Loading subjects…
        </div>
    }

    return (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Name — top of form, prominent */}
            <div>
                <label style={labelStyle}>{vocab.courseSingular} Name *</label>
                <input
                    type="text"
                    placeholder={selectedCat ? selectedCat.name : `e.g. Introductory ${vocab.courseSingular.toLowerCase()}`}
                    value={name}
                    onChange={e => setName(e.target.value)}
                    style={inputStyle}
                    autoFocus
                />
                <p style={{ fontSize: 11, color: EDU.muted, margin: '4px 0 0' }}>
                    This is the name your {vocab.learnerPlural.toLowerCase()} will see. Picking a subject below will pre-fill it — you can always change it.
                </p>
            </div>

            {/* Subject — stepped picker: school type → department → subject */}
            <div>
                <label style={labelStyle}>Subject *</label>
                <div style={{ border: `2px solid ${EDU.border}`, borderRadius: 12, padding: 4, background: EDU.white, maxHeight: 320, overflowY: 'auto' }}>
                    {/* Breadcrumb: Subjects › Root › Department › Subject */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 10px 8px', borderBottom: `1px solid ${EDU.border}`, flexWrap: 'wrap', fontSize: 12 }}>
                        <button type="button" onClick={() => { setSelectedParentId(null); setSelectedDeptId(null); setSelectedCat(null) }}
                            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontWeight: 800, color: selectedParentId ? EDU.muted : EDU.navy, fontSize: 12 }}>
                            Subjects
                        </button>
                        {selectedParentId && (
                            <>
                                <span style={{ color: EDU.muted }}>›</span>
                                <button type="button" onClick={() => { setSelectedDeptId(null); setSelectedCat(null) }}
                                    style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontWeight: 800, color: selectedDeptId ? EDU.muted : EDU.navy, fontSize: 12 }}>
                                    {categories.find(c => c.id === selectedParentId)?.icon} {categories.find(c => c.id === selectedParentId)?.name}
                                </button>
                            </>
                        )}
                        {selectedParentId && selectedDeptId && (
                            <>
                                <span style={{ color: EDU.muted }}>›</span>
                                <button type="button" onClick={() => setSelectedCat(null)}
                                    style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontWeight: 800, color: selectedCat ? EDU.muted : EDU.navy, fontSize: 12 }}>
                                    {categories.find(c => c.id === selectedDeptId)?.name}
                                </button>
                            </>
                        )}
                        {selectedCat && (
                            <>
                                <span style={{ color: EDU.muted }}>›</span>
                                <span style={{ fontWeight: 800, color: EDU.sage, fontSize: 12 }}>{selectedCat.icon} {selectedCat.name}</span>
                                <button type="button" onClick={() => setSelectedCat(null)} title="Change subject" style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: EDU.muted, fontSize: 15, lineHeight: 1 }}>×</button>
                            </>
                        )}
                    </div>

                    {/* Step 1 — school type (root) */}
                    {!selectedParentId && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: 10 }}>
                            {categories.filter(c => !c.parent_id).map(root => (
                                <button key={root.id} type="button" onClick={() => setSelectedParentId(root.id)}
                                    style={{ flex: '1 1 138px', padding: '14px 10px', borderRadius: 12, border: `2px solid ${EDU.border}`, background: EDU.white, cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                                    <span style={{ fontSize: 22 }}>{root.icon}</span>
                                    <span style={{ fontWeight: 800, color: EDU.navy, fontSize: 12.5, lineHeight: 1.2 }}>{root.name}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Step 2 — department (plus root overview) */}
                    {selectedParentId && !selectedDeptId && !selectedCat && (() => {
                        const root = categories.find(c => c.id === selectedParentId)!
                        const depts = categories
                            .filter(c => c.parent_id === selectedParentId)
                        return (
                            <div>
                                <button type="button" onClick={() => { setSelectedCat(root); if (!name) setName(root.name) }}
                                    style={{ width: '100%', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 9, background: 'transparent', border: 'none', borderBottom: `1px solid ${EDU.border}`, cursor: 'pointer', textAlign: 'left' }}>
                                    <span style={{ fontSize: 16 }}>{root.icon}</span>
                                    <span style={{ flex: 1, fontWeight: 700, color: EDU.navy, fontSize: 13.5 }}>{root.name} (Overview)</span>
                                    <ChevronDown size={13} color={EDU.muted} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }} />
                                </button>
                                {depts.map(d => (
                                    <button key={d.id} type="button" onClick={() => setSelectedDeptId(d.id)}
                                        style={{ width: '100%', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 9, background: 'transparent', border: 'none', borderBottom: `1px solid ${EDU.border}`, cursor: 'pointer', textAlign: 'left' }}>
                                        <span style={{ fontSize: 16 }}>{d.icon}</span>
                                        <span style={{ flex: 1, fontWeight: 700, color: EDU.navy, fontSize: 13.5 }}>{d.name}</span>
                                        <ChevronDown size={13} color={EDU.muted} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }} />
                                    </button>
                                ))}
                            </div>
                        )
                    })()}

                    {/* Step 3 — subject (topic, plus department overview) */}
                    {selectedParentId && selectedDeptId && !selectedCat && (() => {
                        const dept = categories.find(c => c.id === selectedDeptId)!
                        const topics = categories
                            .filter(c => c.parent_id === selectedDeptId)
                        return (
                            <div>
                                <button type="button" onClick={() => { setSelectedCat(dept); if (!name) setName(dept.name) }}
                                    style={{ width: '100%', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 9, background: 'transparent', border: 'none', borderBottom: `1px solid ${EDU.border}`, cursor: 'pointer', textAlign: 'left' }}>
                                    <span style={{ fontSize: 16 }}>{dept.icon}</span>
                                    <span style={{ flex: 1, fontWeight: 700, color: EDU.navy, fontSize: 13.5 }}>{dept.name} (Overview)</span>
                                    <ChevronDown size={13} color={EDU.muted} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }} />
                                </button>
                                {topics.map(t => (
                                    <button key={t.id} type="button" onClick={() => { setSelectedCat(t); if (!name) setName(t.name) }}
                                        style={{ width: '100%', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 9, background: 'transparent', border: 'none', borderBottom: `1px solid ${EDU.border}`, cursor: 'pointer', textAlign: 'left' }}>
                                        <span style={{ fontSize: 16 }}>{t.icon}</span>
                                        <span style={{ flex: 1, fontWeight: 600, color: EDU.navy, fontSize: 13.5, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                                        {t.educational_level && (
                                            <span style={{ fontSize: 9, background: `${EDU.sage}15`, color: EDU.sage, padding: '2px 7px', borderRadius: 20, fontWeight: 800, textTransform: 'uppercase', flexShrink: 0 }}>{t.educational_level}</span>
                                        )}
                                    </button>
                                ))}
                                {topics.length === 0 && (
                                    <div style={{ padding: 18, textAlign: 'center', color: EDU.muted, fontSize: 12 }}>No specialised subjects — take the (Overview) above.</div>
                                )}
                            </div>
                        )
                    })()}
                </div>
                <p style={{ fontSize: 11, color: EDU.muted, margin: '4px 0 0' }}>Pick a school type, then a department, then a subject, or take an "(Overview)" to teach the whole department.</p>
            </div>

            {/* (Name field is at top of form — see above) */}

            {/* Target age */}
            <div>
                <label style={labelStyle}>Target Age (optional)</label>
                <input
                    type="number"
                    min="4" max="99"
                    placeholder="e.g. 12"
                    value={targetAge}
                    onChange={e => setTargetAge(e.target.value)}
                    style={inputStyle}
                />
            </div>

            {/* Description */}
            <div>
                <label style={labelStyle}>Description (optional)</label>
                <textarea
                    placeholder={`What will your ${vocab.learnerPlural.toLowerCase()} learn in this ${vocab.courseSingular.toLowerCase()}?`}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={3}
                    style={{
                        ...inputStyle, height: 'auto', padding: '12px 14px',
                        resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5,
                    }}
                />
            </div>

            <button
                type="submit"
                disabled={loading || !selectedCat}
                style={{
                    padding: '14px', borderRadius: 12, background: btnGrad, color: '#fff',
                    border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 4,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    opacity: (loading || !selectedCat) ? 0.6 : 1,
                }}
            >
                {loading ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={18} />}
                Create {vocab.courseSingular}
            </button>
            <p style={{ fontSize: 12, color: EDU.muted, margin: '-6px 0 0', textAlign: 'center', lineHeight: 1.4 }}>
                This creates your organisation's own copy. Your edits stay private to your {vocab.coursePlural.toLowerCase()}.
            </p>
        </form>
    )
}

// ─── Community Library Picker ─────────────────────────────────
function CommunityLibraryPicker({ onClone, loading }: {
    onClone: (course: any) => Promise<void>
    loading: boolean
}) {
    const [courses, setCourses] = useState<any[]>([])
    const [libLoading, setLibLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [selected, setSelected] = useState<any | null>(null)
    const [preview, setPreview] = useState<any | null>(null)
    const [className, setClassName] = useState('')
    const vocab = useOrgVocab()
    const branding = useFacilityBranding()
    const showAge = shouldShowAge(branding?.org_type)

    useEffect(() => {
        supabase
            .from('community_course_library' as any)
            .select('id, name, description, target_age, subject_group, category_id, origin, author_name, origin_facility_name, clone_count, module_count')
            .order('clone_count', { ascending: false })
            .order('name', { ascending: true })
            .limit(60)
            .then(({ data }) => { setCourses((data ?? []) as any[]); setLibLoading(false) })
    }, [])

    // ── Org stream config (Brand Admin → Courses → Catalog Streams) ──
    const [streams, setStreams] = useState<CatalogStream[]>(DEFAULT_STREAMS)
    const { userProfile } = useAuth()
    useEffect(() => { loadOrgCatalogStreams(userProfile?.facility_id).then(setStreams) }, [userProfile?.facility_id])

    const filtered = courses.filter((c: any) =>
        !search ||
        c.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.subject_group?.toLowerCase().includes(search.toLowerCase()) ||
        c.author_name?.toLowerCase().includes(search.toLowerCase())
    )

    const inputStyle: React.CSSProperties = {
        width: '100%', height: 48, padding: '0 14px', borderRadius: 12,
        border: `2px solid ${EDU.border}`, fontSize: 15, outline: 'none',
        boxSizing: 'border-box', background: EDU.white, color: EDU.navy,
    }

    if (!streams.includes('community')) {
        return (
            <div style={{ padding: '32px 20px', textAlign: 'center', background: EDU.cream, borderRadius: 12, border: `2px dashed ${EDU.border}` }}>
                <BookOpen size={28} style={{ margin: '0 auto 10px', display: 'block', color: EDU.muted }} />
                <p style={{ color: EDU.muted, fontSize: 13, margin: 0, lineHeight: 1.5 }}>
                    Community courses are turned off for this organisation.<br />
                    Enable the <strong style={{ color: EDU.navy }}>Community</strong> stream in your admin console → Courses → Catalog Streams.
                </p>
            </div>
        )
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <input
                type="text"
                placeholder="Search: permaculture, archery, first aid, robotics…"
                value={search} onChange={e => setSearch(e.target.value)}
                style={inputStyle}
            />
            {libLoading ? (
                <div style={{ padding: 24, textAlign: 'center', color: EDU.muted, fontSize: 13 }}>
                    <Loader2 size={20} className="animate-spin" style={{ margin: '0 auto 8px', display: 'block' }} /> Loading library…
                </div>
            ) : courses.length === 0 ? (
                <div style={{ padding: '32px 20px', textAlign: 'center', background: EDU.cream, borderRadius: 12, border: `2px dashed ${EDU.border}` }}>
                    <BookOpen size={28} style={{ margin: '0 auto 10px', display: 'block', color: EDU.muted }} />
                    <p style={{ color: EDU.muted, fontSize: 13, margin: 0, lineHeight: 1.5 }}>
                        No community courses yet. Generate a custom course, publish it, and it appears here for every organisation.
                    </p>
                </div>
            ) : (
                <div style={{ maxHeight: 280, overflowY: 'auto', border: `2px solid ${EDU.border}`, borderRadius: 12, padding: 4 }}>
                    {filtered.length === 0 ? (
                        <div style={{ padding: '20px 16px', textAlign: 'center', color: EDU.muted, fontSize: 13 }}>No matches for "{search}"</div>
                    ) : filtered.map((c: any) => (
                        <button key={c.id} type="button"
                            onClick={() => { setSelected(c); setClassName(c.name) }}
                            style={{
                                width: '100%', padding: '10px 12px', display: 'flex',
                                justifyContent: 'space-between', alignItems: 'center', gap: 10,
                                background: selected?.id === c.id ? `${EDU.sage}12` : 'transparent',
                                border: 'none', borderBottom: `1px solid ${EDU.border}`,
                                cursor: 'pointer', textAlign: 'left',
                            }}
                            onMouseEnter={e => { if (selected?.id !== c.id) e.currentTarget.style.background = EDU.cream }}
                            onMouseLeave={e => { if (selected?.id !== c.id) e.currentTarget.style.background = 'transparent' }}
                        >
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                    <span style={{ fontSize: 13.5, fontWeight: selected?.id === c.id ? 800 : 600, color: EDU.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {c.name}
                                    </span>
                                    {c.origin === 'master'
                                        ? <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 20, background: `${EDU.blue}15`, color: EDU.blue, fontWeight: 800, flexShrink: 0 }}>STAFF PICK</span>
                                        : <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 20, background: `${EDU.sage}15`, color: EDU.sage, fontWeight: 800, flexShrink: 0 }}>COMMUNITY</span>
                                    }
                                </div>
                                <div style={{ fontSize: 10.5, color: EDU.muted }}>
                                    {c.origin === 'master' ? 'Still Waters Academy' : `by ${c.author_name}`}
                                    {c.origin_facility_name && c.origin !== 'master' && ` · ${c.origin_facility_name}`}
                                    {' · '}{c.module_count} modules
                                    {c.clone_count > 0 && ` · ${c.clone_count} ${c.clone_count === 1 ? 'classroom' : 'classrooms'} using it`}
                                </div>
                            </div>
                            {showAge && c.target_age && <span style={{ fontSize: 10, color: EDU.muted, flexShrink: 0 }}>Recommended {c.target_age}+</span>}
                        </button>
                    ))}
                </div>
            )}
            {selected && (
                <div style={{ border: `2px solid ${EDU.sage}35`, background: `${EDU.sage}0A`, borderRadius: 12, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: selected.description ? 6 : 0 }}>
                        <span style={{ fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em', color: EDU.sage }}>Selected · tap Preview to see what it covers</span>
                        <button type="button" onClick={() => setPreview(selected)}
                            style={{ border: 'none', background: btnGrad, color: '#fff', padding: '7px 12px', borderRadius: 10, fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                            <Eye size={13} /> Preview
                        </button>
                    </div>
                    {selected.description && (
                        <p style={{ margin: 0, fontSize: 12, color: EDU.slate, lineHeight: 1.55 }}>
                            {renderFormattedText(selected.description.slice(0, 180))}{selected.description.length > 180 ? '…' : ''}
                        </p>
                    )}
                </div>
            )}
            {preview && (
                <CoursePreviewModal
                    course={{ id: preview.id, name: preview.name, source: 'community', description: preview.description, subject_group: preview.subject_group, target_age: preview.target_age }}
                    onClose={() => setPreview(null)}
                />
            )}
            {selected && (
                <>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: EDU.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Your {vocab.courseSingular} Name</label>
                        <input type="text" value={className} onChange={e => setClassName(e.target.value)} style={inputStyle} placeholder={`e.g. ${selected ? selected.name : 'Community course'}: Intro`} />
                    </div>
                    <button
                        onClick={() => onClone({ ...selected, name: className || selected.name })}
                        disabled={loading || !className.trim()}
                        style={{
                            padding: 14, borderRadius: 12, background: btnGrad, color: '#fff', border: 'none',
                            fontWeight: 700, fontSize: 15, cursor: 'pointer',
                            opacity: (loading || !className.trim()) ? 0.6 : 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        }}
                    >
                        {loading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                        Add "{selected.name}" to My {vocab.coursePlural}, Free
                    </button>
                </>
            )}
        </div>
    )
}

// ─── The modal shell — three creation modes ───────────────────
export interface CreateCoursePayload {
    name?: string
    description?: string
    target_age?: number | null
    category_id: string
    source_class_id?: string
    origin?: string
    use_search?: boolean
}

// ─── Generate tab: ONE form — name, subject, age, what to teach ───
// The shell is created by the parent's onCreate (origin: 'generate');
// the platform page then opens the generator on it. No two-step surprise.
function GenerateCourseTab({ loading, hubName, onGenerate }: {
    loading: boolean
    hubName?: string | null
    onGenerate: (v: { name: string; target_age: number | null; category_id: string; description: string; use_search?: boolean }) => Promise<void>
}) {
    const vocab = useOrgVocab()
    const [name, setName] = useState('')
    const [request, setRequest] = useState('')
    const [requestDirty, setRequestDirty] = useState(false)
    const [useSearch, setUseSearch] = useState(false)
    const [categories, setCategories] = useState<EduCategory[]>([])
    const [catLoading, setCatLoading] = useState(true)
    const [selectedCat, setSelectedCat] = useState<EduCategory | null>(null)
    const [selectedParentId, setSelectedParentId] = useState<string | null>(null)
    const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null)
    const [targetAge, setTargetAge] = useState('')

    useEffect(() => {
        // Full taxonomy v3 tree — same source as the Custom tab stepper, so ANY
        // subject can be generated for. Age is generation metadata, NOT a gate.
        supabase
            .from('categories')
            .select('id, name, icon, educational_level, parent_id, target_demographics')
            .eq('is_educational', true)
            .order('name')
            .then(({ data }) => {
                const all = (data || []) as EduCategory[]
                const byId = new Map(all.map(c => [c.id, c] as [string, EduCategory]))
                const inTree = (c: EduCategory): boolean => {
                    let n: EduCategory | undefined = c
                    while (n) {
                        if (n.id.startsWith('70000000-')) return true
                        n = n.parent_id ? byId.get(n.parent_id) : undefined
                    }
                    return false
                }
                setCategories(all.filter(inTree))
                setCatLoading(false)
            })
    }, [])

    // Walk a picked category up to its root: [root, dept?, topic]
    const chainOf = (cat: EduCategory): EduCategory[] => {
        const byId = new Map(categories.map(c => [c.id, c] as [string, EduCategory]))
        const chain: EduCategory[] = [cat]
        let n = cat.parent_id ? byId.get(cat.parent_id) : undefined
        while (n) { chain.unshift(n); n = n.parent_id ? byId.get(n.parent_id) : undefined }
        return chain
    }

    // Pick a subject → prefill name + a build guide the AI reads as context.
    // Only auto-fills the name if the user hasn't typed one yet.
    const pickSubject = (cat: EduCategory) => {
        setSelectedCat(cat)
        // Pre-fill the course name with the subject name only if the user hasn't typed a name yet
        if (!name.trim()) setName(cat.name)
        if (!requestDirty) {
            const chain = chainOf(cat)
            const root = chain.length > 1 ? chain[0].name : null
            const dept = chain.length > 2 ? chain[1].name : null
            const isOverview = chain.length < 3
            const area = [root, dept].filter(Boolean).join(' → ')
            setRequest(
                (area ? `Filed under: ${area}${isOverview ? ' (broad overview)' : ''}\n` : '') +
                (targetAge ? `Audience: around age ${targetAge} — pitch concepts, examples, and depth for that age.\n` : '') +
                `\nBuild the complete course for the course name above: 4–8 modules moving from fundamentals to applied practice, each with 3–6 lessons. Every lesson needs one clear learning objective and 4–6 core concepts. Scaffold difficulty across the course.\n\n(Add anything specific you want covered below this line.)`
            )
        }
    }

    const inputStyle: React.CSSProperties = {
        width: '100%', padding: '12px 14px', borderRadius: 12,
        border: `2px solid ${EDU.border}`, fontSize: 15, outline: 'none',
        boxSizing: 'border-box', background: EDU.white, color: EDU.navy,
    }
    const labelStyle: React.CSSProperties = {
        fontSize: 11, fontWeight: 700, color: EDU.muted,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        display: 'block', marginBottom: 6,
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ fontSize: 13, color: EDU.slate, margin: 0, lineHeight: 1.6 }}>
                Pick a subject below — it pre-fills a build guide — then add anything specific. The AI builds the full {vocab.courseSingular.toLowerCase()} (modules, lessons, concepts, quizzes).
                After creating it, you can add your own documents (SOPs, recipes, standards) for the AI to build and teach from.
            </p>
            <div>
                <label style={labelStyle}>{vocab.courseSingular} Name *</label>
                <input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="e.g. Barista Fundamentals"
                    style={inputStyle}
                    autoFocus
                />
                <p style={{ fontSize: 11, color: EDU.muted, margin: '4px 0 0' }}>
                    Pick a subject below and this will auto-fill — you can always edit it to your own title.
                </p>
            </div>
            {/* Subject — stepped taxonomy picker (same as Custom tab) */}
            <div>
                <label style={labelStyle}>Subject *</label>
                <div style={{ border: `2px solid ${EDU.border}`, borderRadius: 12, padding: 4, background: EDU.white, maxHeight: 260, overflowY: 'auto' }}>
                    {catLoading ? (
                        <div style={{ padding: 24, textAlign: 'center', color: EDU.muted, fontSize: 13 }}>
                            <Loader2 size={20} className="animate-spin" style={{ margin: '0 auto 8px', display: 'block' }} /> Loading subjects…
                        </div>
                    ) : (
                        <>
                            {/* Breadcrumb: Subjects › Root › Department › Subject */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 10px 8px', borderBottom: `1px solid ${EDU.border}`, flexWrap: 'wrap', fontSize: 12 }}>
                                <button type="button" onClick={() => { setSelectedParentId(null); setSelectedDeptId(null); setSelectedCat(null) }}
                                    style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontWeight: 800, color: selectedParentId ? EDU.muted : EDU.navy, fontSize: 12 }}>
                                    Subjects
                                </button>
                                {selectedParentId && (
                                    <>
                                        <span style={{ color: EDU.muted }}>›</span>
                                        <button type="button" onClick={() => { setSelectedDeptId(null); setSelectedCat(null) }}
                                            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontWeight: 800, color: selectedDeptId ? EDU.muted : EDU.navy, fontSize: 12 }}>
                                            {categories.find(c => c.id === selectedParentId)?.icon} {categories.find(c => c.id === selectedParentId)?.name}
                                        </button>
                                    </>
                                )}
                                {selectedParentId && selectedDeptId && (
                                    <>
                                        <span style={{ color: EDU.muted }}>›</span>
                                        <button type="button" onClick={() => setSelectedCat(null)}
                                            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontWeight: 800, color: selectedCat ? EDU.muted : EDU.navy, fontSize: 12 }}>
                                            {categories.find(c => c.id === selectedDeptId)?.name}
                                        </button>
                                    </>
                                )}
                                {selectedCat && (
                                    <>
                                        <span style={{ color: EDU.muted }}>›</span>
                                        <span style={{ fontWeight: 800, color: EDU.sage, fontSize: 12 }}>{selectedCat.icon} {selectedCat.name}</span>
                                        <button type="button" onClick={() => setSelectedCat(null)} title="Change subject" style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: EDU.muted, fontSize: 15, lineHeight: 1 }}>×</button>
                                    </>
                                )}
                            </div>

                            {/* Step 1 — school type (root) */}
                            {!selectedParentId && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: 10 }}>
                                    {categories.filter(c => !c.parent_id).map(root => (
                                        <button key={root.id} type="button" onClick={() => setSelectedParentId(root.id)}
                                            style={{ flex: '1 1 138px', padding: '14px 10px', borderRadius: 12, border: `2px solid ${EDU.border}`, background: EDU.white, cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                                            <span style={{ fontSize: 22 }}>{root.icon}</span>
                                            <span style={{ fontWeight: 800, color: EDU.navy, fontSize: 12.5, lineHeight: 1.2 }}>{root.name}</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Step 2 — department (plus root overview) */}
                            {selectedParentId && !selectedDeptId && !selectedCat && (() => {
                                const root = categories.find(c => c.id === selectedParentId)!
                                const depts = categories.filter(c => c.parent_id === selectedParentId)
                                return (
                                    <div>
                                        <button type="button" onClick={() => pickSubject(root)}
                                            style={{ width: '100%', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 9, background: 'transparent', border: 'none', borderBottom: `1px solid ${EDU.border}`, cursor: 'pointer', textAlign: 'left' }}>
                                            <span style={{ fontSize: 16 }}>{root.icon}</span>
                                            <span style={{ flex: 1, fontWeight: 700, color: EDU.navy, fontSize: 13.5 }}>{root.name} (Overview)</span>
                                            <ChevronDown size={13} color={EDU.muted} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }} />
                                        </button>
                                        {depts.map(d => (
                                            <button key={d.id} type="button" onClick={() => setSelectedDeptId(d.id)}
                                                style={{ width: '100%', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 9, background: 'transparent', border: 'none', borderBottom: `1px solid ${EDU.border}`, cursor: 'pointer', textAlign: 'left' }}>
                                                <span style={{ fontSize: 16 }}>{d.icon}</span>
                                                <span style={{ flex: 1, fontWeight: 700, color: EDU.navy, fontSize: 13.5 }}>{d.name}</span>
                                                <ChevronDown size={13} color={EDU.muted} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }} />
                                            </button>
                                        ))}
                                    </div>
                                )
                            })()}

                            {/* Step 3 — subject (topic, plus department overview) */}
                            {selectedParentId && selectedDeptId && !selectedCat && (() => {
                                const dept = categories.find(c => c.id === selectedDeptId)!
                                const topics = categories.filter(c => c.parent_id === selectedDeptId)
                                return (
                                    <div>
                                        <button type="button" onClick={() => pickSubject(dept)}
                                            style={{ width: '100%', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 9, background: 'transparent', border: 'none', borderBottom: `1px solid ${EDU.border}`, cursor: 'pointer', textAlign: 'left' }}>
                                            <span style={{ fontSize: 16 }}>{dept.icon}</span>
                                            <span style={{ flex: 1, fontWeight: 700, color: EDU.navy, fontSize: 13.5 }}>{dept.name} (Overview)</span>
                                            <ChevronDown size={13} color={EDU.muted} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }} />
                                        </button>
                                        {topics.map(t => (
                                            <button key={t.id} type="button" onClick={() => pickSubject(t)}
                                                style={{ width: '100%', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 9, background: 'transparent', border: 'none', borderBottom: `1px solid ${EDU.border}`, cursor: 'pointer', textAlign: 'left' }}>
                                                <span style={{ fontSize: 16 }}>{t.icon}</span>
                                                <span style={{ flex: 1, fontWeight: 600, color: EDU.navy, fontSize: 13.5, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                                                {t.educational_level && (
                                                    <span style={{ fontSize: 9, background: `${EDU.sage}15`, color: EDU.sage, padding: '2px 7px', borderRadius: 20, fontWeight: 800, textTransform: 'uppercase', flexShrink: 0 }}>{t.educational_level}</span>
                                                )}
                                            </button>
                                        ))}
                                        {topics.length === 0 && (
                                            <div style={{ padding: 18, textAlign: 'center', color: EDU.muted, fontSize: 12 }}>No specialised subjects — take the (Overview) above.</div>
                                        )}
                                    </div>
                                )
                            })()}
                        </>
                    )}
                </div>
                <p style={{ fontSize: 11, color: EDU.muted, margin: '4px 0 0' }}>Pick a school type, then a department, then a subject — it pre-fills the guide below. Any subject, any age.</p>
            </div>

            {/* Target age — generation metadata, NOT a gate */}
            <div>
                <label style={labelStyle}>Target Age (optional)</label>
                <input type="number" min="4" max="99" placeholder="e.g. 12" value={targetAge}
                    onChange={e => { setTargetAge(e.target.value); if (!requestDirty && selectedCat) pickSubject(selectedCat) }}
                    style={inputStyle} />
                <p style={{ fontSize: 11, color: EDU.muted, margin: '4px 0 0' }}>The AI pitches concepts and depth to this age. Everyone can still take the course.</p>
            </div>
            <div>
                <label style={labelStyle}>What should it cover?</label>
                <textarea value={request} onChange={e => { setRequest(e.target.value); setRequestDirty(true) }} rows={3}
                    placeholder="e.g. Our café's full espresso workflow: drink menu, machine settings, milk steaming standards, opening/closing checklists"
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: EDU.slate, cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={useSearch} onChange={(e) => setUseSearch(e.target.checked)} style={{ width: 16, height: 16, accentColor: '#0ea5e9', cursor: 'pointer' }} />
                Use live web search for current facts, prices &amp; versions (adds ~20–30s)
            </label>
            <button onClick={() => onGenerate({ name: name.trim() || selectedCat?.name || 'Untitled course', target_age: parseInt(targetAge) || null, category_id: selectedCat ? selectedCat.id : '', description: request, use_search: useSearch })}
                disabled={loading || !selectedCat}
                style={{ padding: 14, borderRadius: 12, background: btnGrad, color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: (loading || !selectedCat) ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                Create & Generate
            </button>
        </div>
    )
}

export default function CreateCourseModal({ hubName, loading, error, onClose, onCreate }: {
    hubName?: string | null
    loading: boolean
    error?: string | null
    onClose: () => void
    onCreate: (payload: CreateCoursePayload) => Promise<void>
}) {
    const vocab = useOrgVocab()
    const [mode, setMode] = useState<'generate' | 'catalog' | 'community' | 'custom'>('generate')

    // Hide the Community Library tab entirely when the org turned that stream off.
    const [shellStreams, setShellStreams] = useState<CatalogStream[]>(DEFAULT_STREAMS)
    const { userProfile } = useAuth()
    useEffect(() => { loadOrgCatalogStreams(userProfile?.facility_id).then(setShellStreams) }, [userProfile?.facility_id])

    const MODES = [
        { id: 'generate' as const, icon: '✨', label: 'Generate with AI' },
        { id: 'catalog' as const, icon: '📚', label: 'Master Catalog' },
        { id: 'community' as const, icon: '🌍', label: 'Community Library' },
        { id: 'custom' as const, icon: '✏️', label: `Custom ${vocab.courseSingular}` },
    ].filter(m => m.id !== 'community' || shellStreams.includes('community'))

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(6,6,12,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 20, width: '100%', maxWidth: 560, maxHeight: '88dvh', overflowY: 'auto', padding: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ fontSize: 18, fontWeight: 800, color: '#f8fafc', margin: 0 }}>Create New {vocab.courseSingular}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4 }}><X size={20} /></button>
                </div>
                {error && <div style={{ padding: '10px 14px', borderRadius: 12, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: 13, marginBottom: 14 }}>{error}</div>}
                <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
                    {MODES.map(m => (
                        <button key={m.id} onClick={() => setMode(m.id)}
                            style={{ flex: 1, padding: '10px 8px', borderRadius: 12, border: `2px solid ${mode === m.id ? '#0ea5e9' : 'rgba(255,255,255,0.10)'}`, background: mode === m.id ? 'rgba(14,165,233,0.08)' : 'transparent', color: mode === m.id ? '#38bdf8' : '#94a3b8', fontWeight: 800, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            <span>{m.icon}</span> {m.label}
                        </button>
                    ))}
                </div>
                {mode === 'generate' && (
                    <GenerateCourseTab loading={loading} hubName={hubName} onGenerate={async (payload) => { await onCreate({ ...payload, origin: 'generate' }) }} />
                )}
                {mode === 'catalog' && (
                    <CourseCatalogPicker
                        loading={loading}
                        hubName={hubName}
                        onSubmit={async (v) => { await onCreate({ name: v.name, description: v.description, target_age: v.target_age, category_id: v.category_id, source_class_id: v.source_class_id }) }}
                    />
                )}
                {mode === 'community' && (
                    <CommunityLibraryPicker
                        loading={loading}
                        onClone={async (course) => { await onCreate({ name: course.name, category_id: course.category_id, source_class_id: course.id, origin: 'community' }) }}
                    />
                )}
                {mode === 'custom' && (
                    <CreateClassForm
                        loading={loading}
                        onSubmit={async (v) => { await onCreate({ name: v.name, description: v.description, target_age: v.target_age, category_id: v.category_id }) }}
                    />
                )}
            </div>
        </div>
    )
}